import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTree } from '@headless-tree/react';
import { dragAndDropFeature, hotkeysCoreFeature, syncDataLoaderFeature, selectionFeature, expandAllFeature, type TreeInstance, type ItemInstance, type DragTarget } from '@headless-tree/core';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { CategoryGroupFormUtils } from '../../utils/shopCategoryGroupCommon';
import { getCategoryGroupDetail, getShopCountByTreeCodes, CategoryGroupDetail, updateCategoryGroup, updateCategoryGroupTree, CategoryGroupTreeNode as ApiCategoryGroupTreeNode } from '../../utils/shopCategoryGroup';
import { 
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Edit,
  Plus,
  Trash2,
  X,
  FolderTree,
  Save,
  AlertCircle
} from 'lucide-react';


// 카테고리/그룹 트리 노드 인터페이스
interface CategoryGroupTreeNode {
  code: string;        // string, 30, M
  status: string;     // string, 20, M
  depth: number;      // number, M (루트는 0부터 시작)
  count: number;      // number, M (해당 코드에 속한 매장 수)
  title: string;       // string, 30, M
  subtitle?: string;  // string, 30, optional
  logo?: string;      // string, 100, optional
  sub?: CategoryGroupTreeNode[]; // array (하위 노드들)
}

// API 응답을 CategoryGroupTreeNode로 변환 (재귀적으로 sub 처리)
const convertApiNodeToTreeNode = (node: any, depth: number = 0): CategoryGroupTreeNode => {
  return {
    code: node.code,
    status: node.status || 'ACTIVE',
    depth: node.depth !== undefined ? node.depth : depth,
    count: node.count !== undefined ? node.count : 0,
    title: node.title,
    subtitle: node.subtitle,
    logo: node.logo,
    sub: node.sub && Array.isArray(node.sub) && node.sub.length > 0 
      ? node.sub.map((child: any) => convertApiNodeToTreeNode(child, depth + 1))
      : undefined
  };
};

// CategoryGroupTreeNode를 headless-tree 형식으로 변환
const convertToHeadlessTreeData = (nodes: CategoryGroupTreeNode[]): any => {
  const convertNode = (node: CategoryGroupTreeNode): any => {
    const item: any = {
      id: node.code,
      data: node,
      children: node.sub && node.sub.length > 0 
        ? node.sub.map(child => convertNode(child))
        : undefined
    };
    return item;
  };
  
  return nodes.map(node => convertNode(node));
};

// 노드 단계: 1단계(depth 0) ~ 5단계(depth 4)
const MAX_NODE_DEPTH = 4;

const ShopCategoryGroupTreeConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryGroupCode } = useParams<{ categoryGroupCode: string }>();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-category-group-list');

  // Tree state
  const [treeData, setTreeData] = useState<CategoryGroupTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoryGroupNode, setSelectedCategoryGroupNode] = useState<CategoryGroupTreeNode | null>(null);
  const [treeNodeShopCounts, setTreeNodeShopCounts] = useState<Record<string, number>>({});
  const [treeCountsLoaded, setTreeCountsLoaded] = useState(false);


  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editNode, setEditNode] = useState<CategoryGroupTreeNode | null>(null);
  const [parentNode, setParentNode] = useState<CategoryGroupTreeNode | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    status: 'ACTIVE',
    title: '',
    subtitle: '',
    logo: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 변경사항 추적
  const [hasChanges, setHasChanges] = useState(false);
  const [originalTreeData, setOriginalTreeData] = useState<CategoryGroupTreeNode[]>([]);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState<CategoryGroupDetail | null>(null);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 카테고리/그룹 상세 정보 및 트리 데이터 로드 (한 번의 API 호출로 통합)
  useEffect(() => {
    const fetchCategoryGroupData = async () => {
      if (!projectDetail || !categoryGroupCode) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        // 카테고리/그룹 상세 정보 API 호출 (한 번만 호출)
        const categoryGroupDetail = await getCategoryGroupDetail(projectCode, categoryGroupCode);
        
        // 상세 정보 설정
        setSelectedCategoryGroup(categoryGroupDetail);
        
        // tree 필드가 있으면 변환하여 사용
        let initialTreeData: CategoryGroupTreeNode[] = [];
        if (categoryGroupDetail.tree && Array.isArray(categoryGroupDetail.tree)) {
          initialTreeData = categoryGroupDetail.tree.map((node: any) => convertApiNodeToTreeNode(node, 0));
        }
        
        setTreeData(initialTreeData);
        setOriginalTreeData(JSON.parse(JSON.stringify(initialTreeData))); // deep copy

        setTreeCountsLoaded(false);
        const collectNodeCodes = (nodes: CategoryGroupTreeNode[]): string[] => {
          const codes: string[] = [];
          nodes.forEach((n) => {
            if (n.code && n.code !== 'root') codes.push(n.code);
            if (n.sub && n.sub.length > 0) codes.push(...collectNodeCodes(n.sub));
          });
          return codes;
        };
        const nodeCodes = collectNodeCodes(initialTreeData);
        if (nodeCodes.length > 0 && categoryGroupCode) {
          const treeCodes = nodeCodes.map((code) => `${categoryGroupCode}:${code}`);
          try {
            const counts = await getShopCountByTreeCodes(projectCode, treeCodes);
            setTreeNodeShopCounts(counts);
          } catch (countErr) {
            console.error('노드별 매장 수 조회 오류:', countErr);
            setTreeNodeShopCounts({});
          }
        } else {
          setTreeNodeShopCounts({});
        }
      } catch (err) {
        console.error('카테고리/그룹 정보 조회 오류:', err);
        setError(err instanceof Error ? err.message : '카테고리/그룹 정보를 불러오는 중 오류가 발생했습니다.');
        setTreeNodeShopCounts({});
        setSelectedCategoryGroup({
          code: categoryGroupCode,
          title: categoryGroupCode,
          status: 'ACTIVE',
          type: 'CATEGORY'
        } as CategoryGroupDetail);
      } finally {
        setTreeCountsLoaded(true);
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      fetchCategoryGroupData();
    }
  }, [projectDetail, categoryGroupCode]);

  // headless-tree용 데이터 맵 생성
  const treeDataMap = useMemo(() => {
    const map = new Map<string, CategoryGroupTreeNode>();
    const childrenMap = new Map<string, string[]>();
    
    const processNode = (node: CategoryGroupTreeNode, parentId: string | null = null) => {
      map.set(node.code, node);
      const children = node.sub && node.sub.length > 0 
        ? node.sub.map(child => child.code)
        : [];
      childrenMap.set(node.code, children);
      
      if (node.sub) {
        node.sub.forEach(child => processNode(child, node.code));
      }
    };
    
    treeData.forEach(node => processNode(node, null));
    
    // 루트 아이템 ID 목록
    const rootIds = treeData.map(node => node.code);
    childrenMap.set('root', rootIds);
    
    return { map, childrenMap };
  }, [treeData]);

  // 드래그 상태 추적
  const [draggingItemId, setDraggingItemId] = React.useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = React.useState<string | null>(null);
  const [dragOverPosition, setDragOverPosition] = React.useState<'above' | 'below' | 'inside' | null>(null);

  // 노드와 그 서브트리에서 최대 depth 값 반환
  const getMaxDepthInSubtree = useCallback((node: CategoryGroupTreeNode): number => {
    let max = node.depth;
    if (node.sub && node.sub.length > 0) {
      for (const child of node.sub) {
        const d = getMaxDepthInSubtree(child);
        if (d > max) max = d;
      }
    }
    return max;
  }, []);

  // 드롭 가능 여부 체크 헬퍼 함수 (canDrop과 onDrop에서 공통 사용)
  // position: 'inside' | 'above' | 'below' | null - 드롭 위치 (null이면 inside로 간주)
  const canDropItem = useCallback((itemId: string, targetItemId: string, position: 'inside' | 'above' | 'below' | null = null): boolean => {
    // 루트는 타겟이 될 수 없음
    if (targetItemId === 'root') {
      return false;
    }
    
    // 자기 자신으로는 이동할 수 없음
    if (itemId === targetItemId) {
      return false;
    }
    
    // treeData에서 노드 관계 확인
    const findNode = (nodes: CategoryGroupTreeNode[], code: string): CategoryGroupTreeNode | null => {
      for (const node of nodes) {
        if (node.code === code) return node;
        if (node.sub) {
          const found = findNode(node.sub, code);
          if (found) return found;
        }
      }
      return null;
    };
    
    // 노드의 부모 찾기
    const findParent = (nodes: CategoryGroupTreeNode[], code: string, parentCode: string | null = null): string | null => {
      for (const node of nodes) {
        if (node.code === code) return parentCode;
        if (node.sub) {
          const found = findParent(node.sub, code, node.code);
          if (found !== null) return found;
        }
      }
      return null;
    };
    
    const itemNode = findNode(treeData, itemId);
    const targetNode = findNode(treeData, targetItemId);
    
    if (!itemNode || !targetNode) return false;
    
    // 타겟이 자신의 직접/간접 하위 노드인지 확인 (재귀적으로)
    const isDescendant = (parentNode: CategoryGroupTreeNode, childCode: string): boolean => {
      if (parentNode.code === childCode) return true;
      if (parentNode.sub) {
        for (const child of parentNode.sub) {
          if (isDescendant(child, childCode)) return true;
        }
      }
      return false;
    };
    
    // 타겟이 자신의 하위 노드인 경우 드롭 불가 (순환 참조 방지)
    if (isDescendant(itemNode, targetItemId)) {
      return false;
    }
    
    // 타겟이 자신의 직접 부모인 경우
    const itemParentCode = findParent(treeData, itemId);
    if (itemParentCode === targetItemId) {
      // 타겟의 하위로 다시 이동하는 것은 불가 (이미 하위에 있음)
      // 하지만 형제 레벨로 이동하는 것은 허용 (above/below)
      if (position === 'inside') {
        return false;
      }
      // above/below인 경우는 형제 레벨로 이동하므로 허용
      return true;
    }
    
    // 타겟이 자신의 간접 조상인 경우 (할아버지 등) 불가
    if (isDescendant(targetNode, itemId)) {
      return false;
    }

    // 최대 5단계 제한: 드롭 후 이동 노드 서브트리의 최대 depth가 MAX_NODE_DEPTH를 넘으면 불가
    const newDepth = position === 'inside' ? targetNode.depth + 1 : targetNode.depth;
    const maxDepthInMoved = getMaxDepthInSubtree(itemNode);
    const depthOffset = maxDepthInMoved - itemNode.depth;
    const newMaxDepth = newDepth + depthOffset;
    if (newMaxDepth > MAX_NODE_DEPTH) {
      return false;
    }
    
    return true;
  }, [treeData, getMaxDepthInSubtree]);

  // 부모 노드 확장을 위한 ref (모달 추가 또는 드래그앤드롭으로 하위 추가 시)
  const expandParentRef = React.useRef<string | null>(null);

  // headless-tree 인스턴스 생성
  const tree = useTree<CategoryGroupTreeNode>({
    rootItemId: 'root',
    dataLoader: {
      getItem: (itemId: string): CategoryGroupTreeNode => {
        if (itemId === 'root') {
          return { code: 'root', status: 'ACTIVE', depth: -1, count: 0, title: 'Root' };
        }
        return treeDataMap.map.get(itemId) || { 
          code: itemId, status: 'ACTIVE', depth: 0, count: 0, title: '' 
        };
      },
      getChildren: (itemId: string): string[] => {
        return treeDataMap.childrenMap.get(itemId) || [];
      },
    },
    isItemFolder: (item: ItemInstance<CategoryGroupTreeNode>) => {
      const node = item.getItemData();
      return !!(node.sub && node.sub.length > 0);
    },
    getItemName: (item: ItemInstance<CategoryGroupTreeNode>) => {
      const node = item.getItemData();
      return node.title || '';
    },
    features: [
      syncDataLoaderFeature,
      dragAndDropFeature,
      hotkeysCoreFeature,
      selectionFeature,
      expandAllFeature,
    ],
    indent: 24,
    canReorder: true,
    reorderAreaPercentage: 20,
    canDrag: (items: ItemInstance<CategoryGroupTreeNode>[]) => {
      console.log('canDrag called', items);
      if (items.length === 0) return false;
      // 루트는 드래그할 수 없음
      const item = items[0];
      if (item.getId() === 'root') return false;
      return true;
    },
    canDrop: (items: ItemInstance<CategoryGroupTreeNode>[], target: DragTarget<CategoryGroupTreeNode>) => {
      // 드롭 가능 여부 확인
      if (items.length === 0) {
        return false;
      }
      
      const itemToMove = items[0];
      const itemId = itemToMove.getId();
      const targetItem = target.item;
      const targetItemId = targetItem.getId();
      
      // DragTarget에서 position 추출
      const hasChildIndex = 'childIndex' in target && 'insertionIndex' in target;
      const position = hasChildIndex ? ('above' as const) : ('inside' as const);
      
      // 공통 체크 함수 사용
      return canDropItem(itemId, targetItemId, position);
    },
    onDrop: (items: ItemInstance<CategoryGroupTreeNode>[], target: DragTarget<CategoryGroupTreeNode>) => {
          console.log('onDrop called', { items, target });
          
          if (items.length === 0) {
            console.log('No items to move');
            return;
          }
          
          const itemToMove = items[0];
          const itemId = itemToMove.getId();
          const targetItem = target.item;
          const targetItemId = targetItem.getId();
          
          console.log('Moving item:', itemId, 'to target:', targetItemId);
          
          // 루트는 타겟이 될 수 없음
          if (targetItemId === 'root') {
            console.log('Cannot drop on root');
            return;
          }
          
          // 자기 자신으로는 이동할 수 없음
          if (itemId === targetItemId) {
            alert('자기 자신으로는 이동할 수 없습니다.');
            return;
          }
          
          // 자신의 조상(부모, 할아버지 등)으로는 이동할 수 없음 (순환 참조 방지)
          if (itemToMove.isDescendentOf && itemToMove.isDescendentOf(targetItemId)) {
            alert('자신의 상위 노드로는 이동할 수 없습니다.');
            return;
          }
          
          // 타겟이 자신의 하위 노드인지 확인 (타겟이 자신의 자식, 손자 등인 경우)
          if (targetItem.isDescendentOf && targetItem.isDescendentOf(itemId)) {
            alert('자신의 하위 노드로는 이동할 수 없습니다.');
            return;
          }

          // 5단계 제한 등 공통 드롭 가능 여부 (position: item이면 inside, between이면 above)
          const isBetweenItems = 'childIndex' in target && 'insertionIndex' in target;
          const dropPosition = isBetweenItems ? 'above' as const : 'inside' as const;
          if (!canDropItem(itemId, targetItemId, dropPosition)) {
            alert('노드는 최대 5단계까지입니다.');
            return;
          }
          
          // 타겟 정보 추출
          // DragTarget 타입: { item, childIndex, insertionIndex, dragLineIndex, dragLineLevel } | { item }
          const hasChildIndex = isBetweenItems;
          const targetType = hasChildIndex ? 'between-items' : 'item';
          const insertionIndex = hasChildIndex ? (target as { insertionIndex: number }).insertionIndex : undefined;
          
          const hasChildIndexProp = 'childIndex' in target;
          const hasInsertionIndexProp = 'insertionIndex' in target;
          console.log('Target info:', {
            target,
            hasChildIndex,
            targetType,
            insertionIndex,
            hasChildIndexProp,
            hasInsertionIndexProp
          });
          
          // 타겟의 부모 찾기
          const targetParent = targetItem.getParent();
          const targetParentId = targetParent ? targetParent.getId() : 'root';
          
          console.log('Target parent:', targetParentId);
          
          // 타겟 타입에 따라 부모 결정
          let newParentCode: string | null = null;
          let insertionIdx = 0;
          let isChildInsert = false;
          
          if (targetType === 'item') {
            // 타겟 노드의 자식으로 추가 (아이템 위에 드롭)
            newParentCode = targetItemId;
            insertionIdx = 0; // 자식으로 추가할 때는 항상 마지막에 추가
            isChildInsert = true;
            console.log('Dropping as child of:', targetItemId);
          } else {
            // 타겟 노드의 형제로 추가 (아이템 사이에 드롭)
            newParentCode = targetParentId === 'root' ? null : targetParentId;
            // insertionIndex는 타겟 아이템의 형제 중 위치
            // 타겟 아이템의 인덱스를 찾아서 그 위치에 삽입
            if (insertionIndex !== undefined) {
              insertionIdx = insertionIndex;
            } else {
              // insertionIndex가 없으면 타겟 아이템의 현재 인덱스 사용
              const findTargetIndex = (nodes: CategoryGroupTreeNode[], code: string, parentCode: string | null): number => {
                if (parentCode === null) {
                  // 루트 레벨에서 찾기
                  return nodes.findIndex(n => n.code === code);
                } else {
                  // 하위 레벨에서 찾기
                  for (const node of nodes) {
                    if (node.code === parentCode && node.sub) {
                      return node.sub.findIndex(n => n.code === code);
                    }
                    if (node.sub) {
                      const found = findTargetIndex(node.sub, code, parentCode);
                      if (found !== -1) return found;
                    }
                  }
                }
                return -1;
              };
              insertionIdx = findTargetIndex(treeData, targetItemId, targetParentId === 'root' ? null : targetParentId);
              if (insertionIdx === -1) insertionIdx = 0;
            }
            isChildInsert = false;
            console.log('Dropping as sibling at index:', insertionIdx, 'parent:', newParentCode);
          }
          
          console.log('Moving to:', { newParentCode, insertionIdx, isChildInsert });
          
          const newTreeData = moveNodeInTree(
            treeData,
            itemId,
            newParentCode,
            insertionIdx,
            isChildInsert
          );
          
          console.log('New tree data:', newTreeData);
          
          setTreeData(newTreeData);
          setHasChanges(true);
          if (isChildInsert && newParentCode) {
            expandParentRef.current = newParentCode;
          }
    },
  });

  // 최초 로드 시 전체 펼치기
  const hasExpandedOnLoad = React.useRef(false);
  
  // treeData 변경 시 headless-tree에 반영
  useEffect(() => {
    if (tree) {
      tree.rebuildTree();
      
      // 최초 로드 시 전체 펼치기 (한 번만 실행)
      if (!hasExpandedOnLoad.current && treeData.length > 0) {
        hasExpandedOnLoad.current = true;
        setTimeout(() => {
          if (tree && tree.expandAll) {
            tree.expandAll();
          }
        }, 100);
      }
      
      // 부모 노드 확장 처리 (handleSubmit에서 추가한 노드의 경우)
      if (expandParentRef.current) {
        const parentCode = expandParentRef.current;
        expandParentRef.current = null; // 한 번만 실행되도록 초기화
        
        // 트리 재구성 후 부모 노드 찾아서 확장
        setTimeout(() => {
          const items = tree.getItems ? tree.getItems() : [];
          const parentItem = items.find(item => item.getId() === parentCode);
          if (parentItem && parentItem.isFolder && parentItem.isFolder() && !parentItem.isExpanded()) {
            parentItem.expand();
          }
        }, 0);
      }
    }
  }, [treeData, tree]);

  // 트리에서 노드 찾기 (경로로)
  const findNodeByPath = (nodes: CategoryGroupTreeNode[], path: number[]): CategoryGroupTreeNode | null => {
    if (path.length === 0) return null;
    const [first, ...rest] = path;
    if (first >= nodes.length) return null;
    const node = nodes[first];
    if (rest.length === 0) return node;
    if (!node.sub || node.sub.length === 0) return null;
    return findNodeByPath(node.sub, rest);
  };

  // 트리에서 노드 제거 (경로로)
  const removeNodeByPath = (nodes: CategoryGroupTreeNode[], path: number[]): { newNodes: CategoryGroupTreeNode[], removedNode: CategoryGroupTreeNode | null } => {
    if (path.length === 0) return { newNodes: nodes, removedNode: null };
    const [first, ...rest] = path;
    if (first >= nodes.length) return { newNodes: nodes, removedNode: null };
    
    if (rest.length === 0) {
      // 이 레벨에서 제거
      const removedNode = nodes[first];
      const newNodes = nodes.filter((_, index) => index !== first);
      return { newNodes, removedNode };
    }
    
    // 하위 레벨에서 제거
    const node = nodes[first];
    if (!node.sub || node.sub.length === 0) return { newNodes: nodes, removedNode: null };
    
    const { newNodes: newSub, removedNode } = removeNodeByPath(node.sub, rest);
    const updatedNode = { ...node, sub: newSub.length > 0 ? newSub : undefined };
    const newNodes = nodes.map((n, index) => index === first ? updatedNode : n);
    return { newNodes, removedNode };
  };

  // 트리에 노드 삽입 (경로로)
  const insertNodeByPath = (nodes: CategoryGroupTreeNode[], path: number[], node: CategoryGroupTreeNode): CategoryGroupTreeNode[] => {
    if (path.length === 0) {
      // 최상위에 추가
      return [...nodes, node];
    }
    
    const [first, ...rest] = path;
    if (first >= nodes.length) return nodes;
    
    if (rest.length === 0) {
      // 이 레벨에 추가
      const targetNode = nodes[first];
      const updatedNode = { ...targetNode, sub: [...(targetNode.sub || []), node] };
      return nodes.map((n, index) => index === first ? updatedNode : n);
    }
    
    // 하위 레벨에 추가
    const targetNode = nodes[first];
    if (!targetNode.sub) return nodes;
    
    const updatedSub = insertNodeByPath(targetNode.sub, rest, node);
    const updatedNode = { ...targetNode, sub: updatedSub };
    return nodes.map((n, index) => index === first ? updatedNode : n);
  };

  // 노드와 모든 하위 노드의 depth 재계산
  const recalculateDepth = (node: CategoryGroupTreeNode, newDepth: number): CategoryGroupTreeNode => {
    const updatedNode = { ...node, depth: newDepth };
    if (node.sub && node.sub.length > 0) {
      updatedNode.sub = node.sub.map(child => recalculateDepth(child, newDepth + 1));
    }
    return updatedNode;
  };

  // 트리에서 노드를 이동하는 함수
  const moveNodeInTree = (
    nodes: CategoryGroupTreeNode[],
    nodeCode: string,
    newParentCode: string | null,
    newIndex: number,
    isChildInsert: boolean
  ): CategoryGroupTreeNode[] => {
    // 먼저 노드를 찾아서 제거 (하위 노드까지 모두 포함)
    let nodeToMove: CategoryGroupTreeNode | null = null;
    const removeNode = (nodes: CategoryGroupTreeNode[]): CategoryGroupTreeNode[] => {
      return nodes
        .filter(n => {
          if (n.code === nodeCode) {
            nodeToMove = n;
            return false;
          }
          return true;
        })
        .map(n => ({
          ...n,
          sub: n.sub ? removeNode(n.sub) : undefined
        }));
    };
    
    const nodesWithoutNode = removeNode(nodes);
    
    if (!nodeToMove) return nodes;
    
    // depth 재계산
    const newDepth = newParentCode === null || newParentCode === 'root' ? 0 : 
      (() => {
        const findDepth = (nodes: CategoryGroupTreeNode[], code: string, depth: number = 0): number => {
          for (const node of nodes) {
            if (node.code === code) return depth;
            if (node.sub) {
              const found = findDepth(node.sub, code, depth + 1);
              if (found !== -1) return found;
            }
          }
          return -1;
        };
        const found = findDepth(nodesWithoutNode, newParentCode);
        return found !== -1 ? found + 1 : 0;
      })();
    
    // 노드와 모든 하위 노드의 depth 재계산
    const updatedNode = recalculateDepth(nodeToMove, newDepth);
    
    // 새 위치에 삽입
    if (newParentCode === null || newParentCode === 'root') {
      // 루트 레벨에 추가
      const newNodes = [...nodesWithoutNode];
      newNodes.splice(newIndex, 0, updatedNode);
      return newNodes;
    } else {
      // 하위 레벨에 추가
      const insertIntoNode = (nodes: CategoryGroupTreeNode[]): CategoryGroupTreeNode[] => {
        return nodes.map(node => {
          if (node.code === newParentCode) {
            if (isChildInsert) {
              // 타겟 노드의 자식으로 추가 (마지막에 추가)
              const newSub = [...(node.sub || [])];
              newSub.push(updatedNode);
              return { ...node, sub: newSub };
            } else {
              // 타겟 노드의 형제로 추가 (insertionIndex 위치)
              const newSub = [...(node.sub || [])];
              newSub.splice(newIndex, 0, updatedNode);
              return { ...node, sub: newSub };
            }
          }
          if (node.sub) {
            return { ...node, sub: insertIntoNode(node.sub) };
          }
          return node;
        });
      };
      
      return insertIntoNode(nodesWithoutNode);
    }
  };

  // 카테고리/그룹 추가 모달 열기
  const handleAddCategoryGroup = (parent?: CategoryGroupTreeNode) => {
    if (parent && parent.depth >= MAX_NODE_DEPTH) {
      alert('노드는 최대 5단계까지 추가할 수 있습니다.');
      return;
    }
    setEditNode(null);
    setParentNode(parent || null);
    setFormData({
      code: '',
      status: 'ACTIVE',
      title: '',
      subtitle: '',
      logo: ''
    });
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditNode(null);
    setParentNode(null);
    setFormData({
      code: '',
      status: 'ACTIVE',
      title: '',
      subtitle: '',
      logo: ''
    });
    setFormErrors({});
  };

  // 카테고리/그룹 수정 핸들러 (모달 열기)
  const handleEdit = (node: CategoryGroupTreeNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setParentNode(null);
    setEditNode(node);
    setFormData({
      code: node.code,
      status: node.status,
      title: node.title,
      subtitle: node.subtitle || '',
      logo: node.logo || ''
    });
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  // 카테고리/그룹 삭제 핸들러
  const handleDelete = (node: CategoryGroupTreeNode, e: React.MouseEvent) => {
    e.stopPropagation();
    const nodeShopCount = treeCountsLoaded && categoryGroupCode && node.code
      ? (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0)
      : (node.count ?? 0);
    if (nodeShopCount > 0) {
      alert('매장이 있는 카테고리/그룹은 삭제할 수 없습니다.');
      return;
    }
    
    // 하위 노드가 있는 경우 확인
    const hasSubNodes = node.sub && node.sub.length > 0;
    if (hasSubNodes) {
      if (!window.confirm(`"${node.title}" 카테고리/그룹과 하위 항목까지 모두 삭제하시겠습니까?`)) {
        return;
      }
    } else {
      if (!window.confirm(`"${node.title}" 카테고리/그룹을 삭제하시겠습니까?`)) {
        return;
      }
    }
    
    // 트리 데이터에서 제거
    const removeNodeFromTree = (nodes: CategoryGroupTreeNode[], codeToRemove: string): CategoryGroupTreeNode[] => {
      return nodes
        .filter(node => node.code !== codeToRemove)
        .map(node => ({
          ...node,
          sub: node.sub ? removeNodeFromTree(node.sub, codeToRemove) : undefined
        }));
    };
    
    const updatedTreeData = removeNodeFromTree(treeData, node.code);
    setTreeData(updatedTreeData);
    setHasChanges(true);
  };

  // 트리 데이터를 JSON 형식으로 변환 (요구사항 형식)
  const convertTreeToJson = (nodes: CategoryGroupTreeNode[]): ApiCategoryGroupTreeNode[] => {
    return nodes.map(node => {
      const jsonNode: any = {
        code: node.code,
        status: node.status,
        depth: node.depth,
        count: node.count,
        title: node.title,
      };
      
      // 선택적 필드 추가
      if (node.subtitle) {
        jsonNode.subtitle = node.subtitle;
      }
      if (node.logo) {
        jsonNode.logo = node.logo;
      }
      
      // 하위 노드 재귀 처리
      if (node.sub && node.sub.length > 0) {
        jsonNode.sub = convertTreeToJson(node.sub);
      }
      
      return jsonNode as ApiCategoryGroupTreeNode;
    });
  };

  // 저장
  const handleSave = async () => {
    if (!projectDetail || !hasChanges || !categoryGroupCode) return;

    try {
      const projectCode = projectDetail.code || getProjectCode();
      if (!projectCode) {
        throw new Error('프로젝트 코드가 없습니다.');
      }

      // 트리 데이터를 JSON 형식으로 변환
      const jsonTree = convertTreeToJson(treeData);
      
      // 저장하려는 JSON 형식 로그 출력
      const requestBody = { tree: jsonTree };
      console.log('=== 저장하려는 JSON 형식 ===');
      console.log('JSON 문자열:', JSON.stringify(requestBody, null, 2));
      console.log('JSON 객체:', requestBody);
      console.log('트리 데이터 개수:', jsonTree.length);
      console.log('===========================');
      
      // API 호출
      await updateCategoryGroupTree(projectCode, categoryGroupCode, jsonTree);
      
      alert('변경사항이 저장되었습니다.');
      setOriginalTreeData(JSON.parse(JSON.stringify(treeData))); // deep copy
      setHasChanges(false);
    } catch (err) {
      console.error('저장 오류:', err);
      alert(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    }
  };

  // 취소
  const handleCancel = () => {
    if (hasChanges) {
      if (!confirm('변경사항을 취소하시겠습니까?')) {
        return;
      }
    }
    // 원본 데이터로 복원
    setTreeData(JSON.parse(JSON.stringify(originalTreeData))); // deep copy
    setHasChanges(false);
  };

  // 트리 전체에서 사용 중인 노드 코드 목록 수집
  const collectNodeCodes = (nodes: CategoryGroupTreeNode[]): Set<string> => {
    const codes = new Set<string>();
    const visit = (list: CategoryGroupTreeNode[]) => {
      list.forEach(node => {
        codes.add(node.code);
        if (node.sub && node.sub.length > 0) visit(node.sub);
      });
    };
    visit(nodes);
    return codes;
  };

  // 트리에서 노드 한 개 갱신 (code로 찾아서 교체)
  const updateNodeInTree = (nodes: CategoryGroupTreeNode[], targetCode: string, updates: Partial<CategoryGroupTreeNode>): CategoryGroupTreeNode[] => {
    return nodes.map(node => {
      if (node.code === targetCode) {
        return { ...node, ...updates };
      }
      if (node.sub && node.sub.length > 0) {
        return { ...node, sub: updateNodeInTree(node.sub, targetCode, updates) };
      }
      return node;
    });
  };

  // 폼 제출
  const handleSubmit = async () => {
    // 유효성 검사
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) {
      errors.code = '코드를 입력해주세요.';
    } else if (formData.code.length > 20) {
      errors.code = '코드는 20자 이하여야 합니다.';
    } else if (!CategoryGroupFormUtils.CODE_PATTERN.test(formData.code)) {
      errors.code = '코드는 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    } else {
      const existingCodes = collectNodeCodes(treeData);
      if (editNode) {
        // 수정 시: 코드를 바꾸는 경우에만 다른 노드와 중복 검사
        if (formData.code.trim() !== editNode.code && existingCodes.has(formData.code.trim())) {
          errors.code = '이미 사용 중인 코드입니다.';
        }
      } else {
        // 추가 시: 동일 코드가 있으면 불가
        if (existingCodes.has(formData.code.trim())) {
          errors.code = '이미 사용 중인 코드입니다.';
        }
      }
    }
    if (!formData.title.trim()) {
      errors.title = '명칭을 입력해주세요.';
    }
    if (formData.title.length > 50) {
      errors.title = '명칭은 50자 이하여야 합니다.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!projectDetail || !categoryGroupCode) {
      setFormErrors({ submit: '프로젝트 정보 또는 카테고리/그룹 정보가 없습니다.' });
      return;
    }

    const projectCode = projectDetail.code || getProjectCode();
    if (!projectCode) {
      setFormErrors({ submit: '프로젝트 코드가 없습니다.' });
      return;
    }

    try {
      setIsSubmitting(true);
      setFormErrors({});

      // 수정 모드
      if (editNode) {
        await updateCategoryGroup(projectCode, editNode.code, {
          code: formData.code.trim(),
          status: formData.status,
          type: selectedCategoryGroup?.type ?? '',
          title: formData.title.trim(),
          subtitle: formData.subtitle.trim() || undefined
        });
        const updatedTreeData = updateNodeInTree(treeData, editNode.code, {
          code: formData.code.trim(),
          status: formData.status,
          title: formData.title.trim(),
          subtitle: formData.subtitle.trim() || undefined,
          logo: formData.logo.trim() || undefined
        });
        setTreeData(updatedTreeData);
        handleCloseModal();
        return;
      }

      // 추가 모드: depth 계산 (루트는 0부터 시작), 최대 5단계
      let depth = 0;
      if (parentNode && parentNode.code) {
        depth = parentNode.depth + 1;
      }
      if (depth > MAX_NODE_DEPTH) {
        setFormErrors({ submit: '노드는 최대 5단계까지 추가할 수 있습니다.' });
        return;
      }

      // 새 노드 생성 (화면에만 반영, DB 저장은 나중에)
      const newNode: CategoryGroupTreeNode = {
        code: formData.code.trim(),
        status: formData.status,
        depth: depth,
        count: 0, // 초기 매장 수는 0
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim() || undefined,
        logo: formData.logo.trim() || undefined,
        sub: []
      };

      // 트리 데이터에 새 노드 추가
      const addNodeToTree = (nodes: CategoryGroupTreeNode[], newNode: CategoryGroupTreeNode, parentCode?: string): CategoryGroupTreeNode[] => {
        if (!parentCode) {
          // 최상위 노드로 추가
          return [...nodes, newNode];
        }
        
        return nodes.map(node => {
          if (node.code === parentCode) {
            const updatedSub = [...(node.sub || []), newNode];
            return { ...node, sub: updatedSub };
          }
          if (node.sub && node.sub.length > 0) {
            return { ...node, sub: addNodeToTree(node.sub, newNode, parentCode) };
          }
          return node;
        });
      };

      const updatedTreeData = addNodeToTree(treeData, newNode, parentNode?.code);
      setTreeData(updatedTreeData);
      setHasChanges(true);
      
      // 부모 노드가 있으면 확장하도록 ref 설정
      if (parentNode) {
        expandParentRef.current = parentNode.code;
      }

      handleCloseModal();
      
    } catch (err) {
      console.error(editNode ? '카테고리/그룹 수정 오류:' : '카테고리/그룹 생성 오류:', err);
      setFormErrors({ 
        submit: err instanceof Error ? err.message : (editNode ? '카테고리/그룹 수정 중 오류가 발생했습니다.' : '카테고리/그룹 생성 중 오류가 발생했습니다.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 통계 계산
  const categoryGroupStats = {
    totalItems: treeData.reduce((count, node) => {
      const countChildren = (n: CategoryGroupTreeNode): number => {
        return 1 + (n.sub?.reduce((sum, child) => sum + countChildren(child), 0) || 0);
      };
      return count + countChildren(node);
    }, 0),
    topLevelItems: treeData.length,
    activeItems: treeData.reduce((count, node) => {
      const countActive = (n: CategoryGroupTreeNode): number => {
        const isActive = n.status === 'ACTIVE' ? 1 : 0;
        return isActive + (n.sub?.reduce((sum, child) => sum + countActive(child), 0) || 0);
      };
      return count + countActive(node);
    }, 0)
  };


  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="카테고리/그룹 트리 편집"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* 컨텐츠 영역 */}
        <main className="flex-1 flex flex-col min-h-0 p-4 sm:p-6 overflow-y-auto">
          {/* 카테고리/그룹 정보 영역 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex-shrink-0">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                {selectedCategoryGroup && (
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      <FolderTree className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{selectedCategoryGroup.title}</h3>
                      <p className="text-xs text-gray-500">{selectedCategoryGroup.brandName}({selectedCategoryGroup.code})</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/shops/category-groups')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>목록으로</span>
                </button>
              </div>
            </div>

            {/* 변경사항 관리 영역 */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {hasChanges && (
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">저장되지 않은 변경사항이 있습니다</span>
                    </div>
                  )}
                  {!hasChanges && (
                    <span className="text-sm text-gray-500">변경사항이 없습니다</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={!hasChanges}
                    className={`px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${
                      !hasChanges ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    변경취소
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!hasChanges}
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors ${
                      !hasChanges ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Save className="w-5 h-5 mr-2" />
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full">
            {/* Filter/Action Area */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                {/* 액션 버튼 */}
                <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
                  <button
                    onClick={() => {
                      if (tree && tree.expandAll) {
                        tree.expandAll();
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    title="전체 펼치기"
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    전체 펼치기
                  </button>
                  <button
                    onClick={() => {
                      if (tree && tree.collapseAll) {
                        tree.collapseAll();
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    title="전체 접기"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    전체 접기
                  </button>
                  <button
                    onClick={() => handleAddCategoryGroup()}
                    className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    카테고리/그룹 추가
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{categoryGroupStats.totalItems}</span>개 항목
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    활성 항목: <span className="font-medium text-gray-900 ml-1">{categoryGroupStats.activeItems}</span>
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                    최상위 항목: <span className="font-medium text-gray-900 ml-1">{categoryGroupStats.topLevelItems}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Category Group Tree List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 lg:p-6">
              {/* Loading State */}
              {isLoading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="text-red-500 mb-4">
                    <Trash2 className="w-12 h-12 mx-auto" />
                  </div>
                  <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                  <button
                    onClick={() => {
                      setError(null);
                      // TODO: 카테고리/그룹 트리 다시 불러오기
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    다시 시도
                  </button>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && !error && treeData.length === 0 && (
                <div className="text-center py-12">
                  <FolderTree className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">등록된 카테고리/그룹이 없습니다.</p>
                  <button
                    onClick={() => handleAddCategoryGroup()}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    카테고리/그룹 추가
                  </button>
                </div>
              )}

              {/* Tree Data */}
              {!isLoading && !error && treeData.length > 0 && (
                <div 
                  {...tree.getContainerProps()}
                  className="space-y-1 relative min-h-[400px]"
                  style={{ minHeight: '400px' }}
                >
                  {/* 드래그 라인 표시 */}
                  {tree.getDragLineData() && (() => {
                    const dragLineData = tree.getDragLineData();
                    if (!dragLineData) return null;
                    const style = tree.getDragLineStyle();
                    return (
                      <div
                        className="absolute bg-blue-500 z-50 pointer-events-none"
                        style={{
                          ...style,
                          height: '2px',
                        }}
                      />
                    );
                  })()}
                  {tree.getItems().map((item) => {
                    const node = item.getItemData() as CategoryGroupTreeNode;
                    const level = item.getItemMeta().level;
                    const isExpanded = item.isExpanded();
                    const isSelected = item.isSelected ? item.isSelected() : false;
                    const hasChildren = item.isFolder();
                    
                    // 루트 아이템은 렌더링하지 않음
                    if (item.getId() === 'root') return null;
                    
                    // 드래그 상태 확인 (headless-tree가 제공하는 메서드 사용)
                    const isDragTarget = item.isDragTarget ? item.isDragTarget() : false;
                    const isUnorderedDragTarget = item.isUnorderedDragTarget ? item.isUnorderedDragTarget() : false;
                    const isDraggingOver = item.isDraggingOver ? item.isDraggingOver() : false;
                    const isDragTargetAbove = item.isDragTargetAbove ? item.isDragTargetAbove() : false;
                    const isDragTargetBelow = item.isDragTargetBelow ? item.isDragTargetBelow() : false;
                    
                    // tree.getDragTarget()을 사용해서 현재 드래그 타겟 확인
                    const currentDragTarget = tree.getDragTarget ? tree.getDragTarget() : null;
                    const isCurrentDragTarget = currentDragTarget && currentDragTarget.item.getId() === item.getId();
                    
                    // 드래그 타겟 표시는 하나만 (isUnorderedDragTarget 우선, 그 다음 isDragTarget, 그 다음 currentDragTarget)
                    const showDragTarget = isUnorderedDragTarget || (isDragTarget && !isDragTargetAbove && !isDragTargetBelow) || isCurrentDragTarget;
                    
                    // 디버깅: 드래그 상태 로그 (드래그 중일 때만)
                    if (currentDragTarget) {
                      console.log('Drag state for', item.getId(), {
                        isDragTarget,
                        isUnorderedDragTarget,
                        isDraggingOver,
                        isDragTargetAbove,
                        isDragTargetBelow,
                        isCurrentDragTarget,
                        showDragTarget,
                        currentDragTarget: currentDragTarget ? currentDragTarget.item.getId() : null
                      });
                    }
                    
                    return (
                      <React.Fragment key={item.getId()}>
                        {/* 드래그 타겟 위쪽 표시 */}
                        {isDragTargetAbove && (
                          <div 
                            className="h-0.5 bg-blue-500 mx-4"
                            style={{ marginLeft: `${level * 24 + 16}px` }}
                          />
                        )}
                        <div
                          {...item.getProps()}
                          draggable={true}
                          onDragStart={(e) => {
                            console.log('onDragStart', item.getId());
                            setDraggingItemId(item.getId());
                            // headless-tree의 드래그 시작 이벤트도 호출
                            const props = item.getProps();
                            if (props.onDragStart) {
                              props.onDragStart(e);
                            }
                          }}
                          onDragEnd={(e) => {
                            console.log('onDragEnd', item.getId());
                            setDraggingItemId(null);
                            setDragOverItemId(null);
                            setDragOverPosition(null);
                            // headless-tree의 드래그 종료 이벤트도 호출
                            const props = item.getProps();
                            if (props.onDragEnd) {
                              props.onDragEnd(e);
                            }
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (draggingItemId && draggingItemId !== item.getId() && item.getId() !== 'root') {
                              const itemId = item.getId();
                              
                              // 드롭 가능 여부 체크 (position은 나중에 결정되므로 null로 전달)
                              // 실제 체크는 onDrop에서 dragOverPosition과 함께 수행됨
                              if (!canDropItem(draggingItemId, itemId, null)) {
                                return;
                              }
                              
                              const rect = e.currentTarget.getBoundingClientRect();
                              const y = e.clientY - rect.top;
                              const height = rect.height;
                              
                              // 위쪽 30% 영역
                              if (y < height * 0.3) {
                                setDragOverItemId(itemId);
                                setDragOverPosition('above');
                              }
                              // 아래쪽 30% 영역
                              else if (y > height * 0.7) {
                                setDragOverItemId(itemId);
                                setDragOverPosition('below');
                              }
                              // 중간 영역 (자식으로 추가)
                              else {
                                setDragOverItemId(itemId);
                                setDragOverPosition('inside');
                              }
                            }
                          }}
                          onDragLeave={(e) => {
                            // 같은 요소 내에서만 leave 처리
                            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                              setDragOverItemId(null);
                              setDragOverPosition(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            console.log('onDrop triggered', {
                              draggingItemId,
                              dragOverItemId: item.getId(),
                              dragOverPosition
                            });
                            
                            if (draggingItemId && dragOverItemId === item.getId() && dragOverPosition && item.getId() !== 'root') {
                              // 실제 이동 로직 실행
                              const itemId = draggingItemId;
                              const targetItemId = item.getId();
                              
                              // 드롭 가능 여부 체크
                              if (!canDropItem(itemId, targetItemId, dragOverPosition)) {
                                const itemNode = treeDataMap.map.get(itemId);
                                const targetNode = treeDataMap.map.get(targetItemId);
                                const isDepthExceeded = itemNode && targetNode && (() => {
                                  const newDepth = dragOverPosition === 'inside' ? targetNode.depth + 1 : targetNode.depth;
                                  const maxInMoved = getMaxDepthInSubtree(itemNode);
                                  return newDepth + (maxInMoved - itemNode.depth) > MAX_NODE_DEPTH;
                                })();
                                alert(isDepthExceeded ? '노드는 최대 5단계까지입니다.' : '자기 자신이나 하위 노드로는 이동할 수 없습니다.');
                                setDraggingItemId(null);
                                setDragOverItemId(null);
                                setDragOverPosition(null);
                                return;
                              }
                              
                              // 타겟 타입에 따라 부모 결정
                              let newParentCode: string | null = null;
                              let insertionIdx = 0;
                              let isChildInsert = false;
                              
                              // 타겟의 부모 찾기
                              const findParentCode = (nodes: CategoryGroupTreeNode[], targetCode: string, parentCode: string | null = null): string | null => {
                                for (const node of nodes) {
                                  if (node.code === targetCode) {
                                    return parentCode;
                                  }
                                  if (node.sub) {
                                    const found = findParentCode(node.sub, targetCode, node.code);
                                    if (found !== null) return found;
                                  }
                                }
                                return null;
                              };
                              
                              const targetParentCode = findParentCode(treeData, targetItemId);
                              
                              if (dragOverPosition === 'inside') {
                                // 타겟 노드의 자식으로 추가
                                newParentCode = targetItemId;
                                insertionIdx = 0;
                                isChildInsert = true;
                              } else {
                                // 타겟 노드의 형제로 추가
                                newParentCode = targetParentCode;
                                // insertionIndex 계산
                                const findTargetIndex = (nodes: CategoryGroupTreeNode[], code: string, parentCode: string | null): number => {
                                  if (parentCode === null) {
                                    return nodes.findIndex(n => n.code === code);
                                  } else {
                                    for (const node of nodes) {
                                      if (node.code === parentCode && node.sub) {
                                        return node.sub.findIndex(n => n.code === code);
                                      }
                                      if (node.sub) {
                                        const found = findTargetIndex(node.sub, code, parentCode);
                                        if (found !== -1) return found;
                                      }
                                    }
                                  }
                                  return -1;
                                };
                                const targetIndex = findTargetIndex(treeData, targetItemId, targetParentCode);
                                insertionIdx = dragOverPosition === 'above' ? targetIndex : targetIndex + 1;
                                isChildInsert = false;
                              }
                              
                              const newTreeData = moveNodeInTree(
                                treeData,
                                itemId,
                                newParentCode,
                                insertionIdx,
                                isChildInsert
                              );
                              
                              console.log('Moving node', { itemId, newParentCode, insertionIdx, isChildInsert });
                              
                              setTreeData(newTreeData);
                              setHasChanges(true);
                              if (isChildInsert && newParentCode) {
                                expandParentRef.current = newParentCode;
                              }
                            }
                            
                            setDraggingItemId(null);
                            setDragOverItemId(null);
                            setDragOverPosition(null);
                          }}
                          className={`flex items-center py-3 px-4 rounded-lg transition-all duration-200 cursor-move ${
                            isSelected ? 'bg-blue-50 border border-blue-200' : ''
                          } ${
                            dragOverItemId === item.getId() && dragOverPosition === 'inside' ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-300 shadow-lg' : ''
                          } ${
                            dragOverItemId === item.getId() && dragOverPosition === 'above' ? 'border-t-4 border-blue-500 bg-blue-50' : ''
                          } ${
                            dragOverItemId === item.getId() && dragOverPosition === 'below' ? 'border-b-4 border-blue-500 bg-blue-50' : ''
                          } ${
                            showDragTarget ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-300' : ''
                          } ${
                            isDraggingOver ? 'bg-blue-50 border-2 border-blue-300' : ''
                          } ${
                            !isSelected && !showDragTarget && !isDraggingOver && dragOverItemId !== item.getId() ? 'hover:bg-gray-50' : ''
                          }`}
                          style={{ paddingLeft: `${level * 24 + 16}px` }}
                        >
                        {/* 펼침/접힘 아이콘 */}
                        <div className="flex-shrink-0 w-8 flex items-center justify-center">
                          {hasChildren ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isExpanded) {
                                  item.collapse();
                                } else {
                                  item.expand();
                                }
                              }}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                            </button>
                          ) : (
                            <div className="w-4 h-4" />
                          )}
                        </div>

                        {/* 카테고리/그룹 아이콘 또는 로고 */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden mr-3">
                          {node.logo ? (
                            <img 
                              src={node.logo} 
                              alt={node.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <FolderTree className="w-5 h-5 text-gray-400" />
                          )}
                        </div>

                        {/* 카테고리/그룹명 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{node.title}</span>
                            {node.subtitle && (
                              <span className="text-xs text-gray-500">({node.subtitle})</span>
                            )}
                            {node.code && (
                              <span className="text-xs text-gray-400">{node.code}</span>
                            )}
                            {node.code && node.code !== 'root' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                매장: {!treeCountsLoaded ? '-' : (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0)}
                              </span>
                            )}
                            {node.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                node.status === 'ACTIVE' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {node.status === 'ACTIVE' ? '활성' : '비활성'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 액션 아이콘 */}
                        <div className="flex-shrink-0 flex items-center space-x-2 ml-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(node, e);
                            }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                            title="수정"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddCategoryGroup(node);
                            }}
                            disabled={node.depth >= MAX_NODE_DEPTH}
                            className={`p-1.5 rounded-lg transition-colors ${
                              node.depth >= MAX_NODE_DEPTH
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                            }`}
                            title={node.depth >= MAX_NODE_DEPTH ? '노드는 최대 5단계까지 추가할 수 있습니다' : '하위 카테고리/그룹 추가'}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(node, e);
                            }}
                            disabled={(treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0) : (node.count ?? 0)) > 0}
                            className={`p-1.5 rounded-lg transition-colors ${
                              (treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0) : (node.count ?? 0)) > 0
                                ? 'text-gray-300 cursor-not-allowed' 
                                : 'text-red-600 hover:text-red-700 hover:bg-gray-100'
                            }`}
                            title={(treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${node.code}`] ?? 0) : (node.count ?? 0)) > 0 ? '매장이 있는 경우 삭제할 수 없습니다' : '삭제'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                        {/* 드래그 타겟 아래쪽 표시 */}
                        {isDragTargetBelow && (
                          <div 
                            className="h-0.5 bg-blue-500 mx-4"
                            style={{ marginLeft: `${level * 24 + 16}px` }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* 변경사항 알림 */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6 bg-amber-100 border-2 border-amber-400 rounded-lg px-4 py-3 shadow-lg">
            <p className="text-sm font-medium text-amber-900">
              저장되지 않은 변경사항이 있습니다
            </p>
          </div>
        )}
      </div>

      {/* 카테고리/그룹 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editNode ? '카테고리/그룹 수정' : parentNode ? '하위 카테고리/그룹 추가' : '카테고리/그룹 추가'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 모달 본문 */}
            <div className="p-6 space-y-4">
              {parentNode && !editNode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    부모 카테고리/그룹: <span className="font-medium">{parentNode.title}</span>
                    {parentNode.code && (
                      <span className="text-blue-600 ml-1">({parentNode.code})</span>
                    )}
                  </p>
                </div>
              )}

              {/* 코드: 매장 수가 0일 때만 수정 가능 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  코드 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const value = raw.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20);
                    setFormData(prev => ({ ...prev, code: value }));
                    if (formErrors.code) {
                      setFormErrors(prev => ({ ...prev, code: '' }));
                    }
                  }}
                  placeholder="영문, 숫자, _, - 만 입력 (최대 20자)"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.code ? 'border-red-500' : 'border-gray-300'
                  } ${editNode && (treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${editNode.code}`] ?? 0) : editNode.count) > 0 ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                  maxLength={20}
                  disabled={!!(editNode && (treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${editNode.code}`] ?? 0) : editNode.count) > 0)}
                />
                {editNode && (treeCountsLoaded ? (treeNodeShopCounts[`${categoryGroupCode}:${editNode.code}`] ?? 0) : editNode.count) > 0 && (
                  <p className="mt-1 text-xs text-gray-500">매장이 있는 경우 코드를 수정할 수 없습니다.</p>
                )}
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.code}</p>
                )}
              </div>

              {/* 상태 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상태
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ACTIVE">활성</option>
                  <option value="INACTIVE">비활성</option>
                </select>
              </div>

              {/* 명칭 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  명칭 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, title: e.target.value }));
                    if (formErrors.title) {
                      setFormErrors(prev => ({ ...prev, title: '' }));
                    }
                  }}
                  placeholder="명칭 입력 (최대 50자)"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  maxLength={50}
                />
                {formErrors.title && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.title}</p>
                )}
              </div>

              {/* 부제 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  부제
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, subtitle: e.target.value }));
                  }}
                  placeholder="부제 입력 (최대 30자, 선택사항)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={30}
                />
              </div>

              {/* 로고 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  로고 URL
                </label>
                <input
                  type="text"
                  value={formData.logo}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, logo: e.target.value }));
                  }}
                  placeholder="로고 이미지 URL 입력 (최대 100자, 선택사항)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>

              {/* 에러 메시지 */}
              {formErrors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{formErrors.submit}</p>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {editNode ? '수정 중...' : '생성 중...'}
                  </>
                ) : editNode ? (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    수정
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    생성
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopCategoryGroupTreeConfig;